import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus, FiRefreshCw, FiSearch, FiUpload, FiX } from 'react-icons/fi';
import { LoadingSpinner } from '../LoadingSpinner';
import { ConfirmModal } from '../SharedModal';
import '../SharedModal/ConfirmModal.css';
import SessionService from '../../services/SessionService';
import { resolveGroupParticipantsErrorMessage } from '../../utils/broadcastContactUtils';
import { listUnifiedAgents, type UnifiedAiAgentDTO } from '../../services/UnifiedAIAgentService';
import { getColumns, type Column } from '../../services/ColumnService';
import { getContactsPaged } from '../../services/ContactService';
import type { ContactListDTO } from '../../types/pagedAndDtos';
import {
  cancelSparkCampaign,
  createSparkCampaign,
  getSparkCampaign,
  getSparkCampaignExecution,
  getSparkTemplate,
  listSparkCampaigns,
  listSparkEntrypoints,
  listSparkTemplates,
  parseSparkCampaignExcel,
  previewSparkAudience,
  startSparkCampaign,
  type SparkCampaignItem,
  type SparkCampaignRecipientInput,
  type SparkEntrypointOption,
  type SparkExecutionSummary,
  type SparkMessageTemplateDetail,
  type SparkMessageTemplateSummary,
} from '../../services/SparkCampaignService';
import { overLimitInfo } from '../../utils/campaignOverLimit';
import SparkMessageTemplatesPanel from './SparkMessageTemplatesPanel';

type Props = {
  sectorId: number;
  onSwitchToEmail?: () => void;
  hasEmailModule?: boolean;
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  pending: 'Rascunho',
  processing: 'Enviando',
  running: 'Enviando',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  failed: 'Com falha',
  paused: 'Pausada',
};

const RECIPIENT_STATUS_LABEL: Record<string, string> = {
  pending: 'Na fila',
  sending: 'Enviando',
  sent: 'Enviada',
  delivered: 'Entregue',
  read: 'Lida',
  failed: 'Com falha',
  skipped: 'Pulado',
  cancelled: 'Cancelado',
};

const AUDIENCE_LABEL: Record<string, string> = {
  crm: 'Funil do CRM',
  excel: 'Planilha',
  mixed: 'CRM + planilha',
};

function statusClass(status: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'processing' || s === 'running') return 'status-badge running';
  if (s === 'canceled') return 'status-badge cancelled';
  if (s === 'completed' || s === 'cancelled' || s === 'failed' || s === 'pending') return `status-badge ${s}`;
  return 'status-badge pending';
}

function templatePreviewText(detail: SparkMessageTemplateDetail | null): string {
  if (!detail) return '';
  const parts: string[] = [];
  if (detail.header?.format === 'TEXT' && (detail.header.text || '').trim()) parts.push(detail.header.text!.trim());
  else if (detail.header?.format && detail.header.format !== 'TEXT') parts.push(`[${detail.header.format}]`);
  if ((detail.body || '').trim()) parts.push(detail.body!.trim());
  if ((detail.footer || '').trim()) parts.push(detail.footer!.trim());
  if (detail.buttons?.length) parts.push(detail.buttons.map((button) => `[${button.text}]`).join('  '));
  if (parts.length) return parts.join('\n\n');
  const items = detail.items || [];
  const texts = items
    .filter((i) => (i.type || 'text').toLowerCase() === 'text' && (i.text || '').trim())
    .map((i) => (i.text || '').trim());
  return texts.join('\n\n');
}

const SparkCampaignsPanel: React.FC<Props> = ({ sectorId, onSwitchToEmail, hasEmailModule }) => {
  const [items, setItems] = useState<SparkCampaignItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{
    campaign: SparkCampaignItem & { commonVariables?: Record<string, string> };
    recipients: Array<{ id: number; name?: string; phone: string; contactId?: number; enrolledAt?: string; status?: string | null; error?: string | null }>;
    execution?: SparkExecutionSummary | null;
  } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [templates, setTemplates] = useState<SparkMessageTemplateSummary[]>([]);
  const [templateDetail, setTemplateDetail] = useState<SparkMessageTemplateDetail | null>(null);
  const [aiAgents, setAiAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [entrypoints, setEntrypoints] = useState<SparkEntrypointOption[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);

  const [formName, setFormName] = useState('');
  const [formTemplateId, setFormTemplateId] = useState('');
  const [formCadence, setFormCadence] = useState(10);
  const [formVars, setFormVars] = useState<Record<string, string>>({});
  const [formRecipients, setFormRecipients] = useState<SparkCampaignRecipientInput[]>([]);
  const [formColumnIds, setFormColumnIds] = useState<Set<number>>(new Set());
  const [formContactIds, setFormContactIds] = useState<Set<number>>(new Set());
  const [formEntrypointId, setFormEntrypointId] = useState('');
  const [formAiAgentId, setFormAiAgentId] = useState<number | null>(null);
  const [formAiReplyEnabled, setFormAiReplyEnabled] = useState(true);
  const [excelImporting, setExcelImporting] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewNote, setPreviewNote] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<ContactListDTO[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);

  const [confirmAction, setConfirmAction] = useState<'start' | 'cancel' | 'over_limit' | null>(null);
  const [overLimitMessage, setOverLimitMessage] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listSparkCampaigns(sectorId);
      setItems(list);
    } catch (e) {
      setError(resolveGroupParticipantsErrorMessage(e, 'Não foi possível carregar as campanhas'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [sectorId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const d = await getSparkCampaign(sectorId, id);
      setDetail({
        campaign: d.campaign,
        recipients: d.recipients ?? [],
        execution: d.execution ?? null,
      });
    } catch (e) {
      setError(resolveGroupParticipantsErrorMessage(e, 'Não foi possível carregar a campanha'));
    } finally {
      setDetailLoading(false);
    }
  }, [sectorId]);

  useEffect(() => {
    if (selectedId == null) {
      setDetail(null);
      return;
    }
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const status = (detail?.campaign.status || '').toLowerCase();
    if (!selectedId || (status !== 'processing' && status !== 'running')) return;
    pollRef.current = setInterval(async () => {
      try {
        const exec = await getSparkCampaignExecution(sectorId, selectedId);
        const remoteStatus = (exec.execution?.status || '').toLowerCase();
        setDetail((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            execution: exec.execution ?? prev.execution,
            campaign: remoteStatus ? { ...prev.campaign, status: remoteStatus } : prev.campaign,
          };
        });
        if (remoteStatus) {
          setItems((prev) =>
            prev.map((it) => (it.id === selectedId ? { ...it, status: remoteStatus } : it))
          );
        }
      } catch {
        /* ignore poll errors */
      }
    }, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [selectedId, detail?.campaign.status, sectorId]);

  const resetForm = () => {
    setFormError(null);
    setFormName('');
    setFormTemplateId('');
    setFormCadence(10);
    setFormVars({});
    setFormRecipients([]);
    setFormColumnIds(new Set());
    setFormContactIds(new Set());
    setFormEntrypointId('');
    setFormAiAgentId(null);
    setFormAiReplyEnabled(true);
    setPreviewCount(null);
    setTemplateDetail(null);
    setContactSearch('');
    setContactResults([]);
    setContactsError(null);
  };

  const openCreate = async () => {
    setShowCreate(true);
    resetForm();
    try {
      const tok = SessionService.getToken();
      const [tpls, agents, eps, cols] = await Promise.all([
        listSparkTemplates(sectorId),
        tok ? listUnifiedAgents(sectorId, tok, 'campaign') : Promise.resolve([]),
        listSparkEntrypoints(sectorId),
        getColumns(),
      ]);
      setTemplates(tpls);
      setAiAgents((agents || []).filter((a: UnifiedAiAgentDTO) => a.isEnabled !== false));
      setEntrypoints(eps);
      if (eps.length === 1) setFormEntrypointId(eps[0].entrypointId);
      setColumns(cols);
    } catch (e) {
      setFormError(resolveGroupParticipantsErrorMessage(e, 'Não foi possível carregar os modelos de mensagem'));
    }
  };

  useEffect(() => {
    if (!formTemplateId) {
      setTemplateDetail(null);
      return;
    }
    let cancelled = false;
    getSparkTemplate(sectorId, formTemplateId)
      .then((d) => {
        if (cancelled) return;
        setTemplateDetail(d);
        const vars: Record<string, string> = {};
        for (const v of d.commonVariables || []) vars[v.key] = '';
        setFormVars(vars);
      })
      .catch(() => {
        if (!cancelled) setTemplateDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [formTemplateId, sectorId]);

  useEffect(() => {
    if (!showCreate) return;
    const q = contactSearch.trim();
    let cancelled = false;
    const t = setTimeout(async () => {
      setContactsLoading(true);
      setContactsError(null);
      try {
        const res = await getContactsPaged(sectorId, 1, 40, q || undefined);
        if (!cancelled) setContactResults(res.items ?? []);
      } catch (e) {
        if (!cancelled) {
          setContactResults([]);
          setContactsError(resolveGroupParticipantsErrorMessage(e, 'Não foi possível carregar os contatos'));
        }
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [contactSearch, sectorId, showCreate]);

  const toggleColumn = (id: number) => {
    setFormColumnIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setPreviewCount(null);
  };

  const toggleContact = (id: number) => {
    setFormContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setPreviewCount(null);
  };

  const audiencePayload = useMemo(
    () => ({
      platform: 'whatsapp' as const,
      entrypointId: formEntrypointId || undefined,
      recipients: formRecipients,
      contactIds: Array.from(formContactIds),
      columnIds: Array.from(formColumnIds),
    }),
    [formEntrypointId, formRecipients, formContactIds, formColumnIds]
  );

  const handleExcel = async (file: File | null) => {
    if (!file) return;
    setExcelImporting(true);
    setFormError(null);
    try {
      const res = await parseSparkCampaignExcel(sectorId, file);
      setFormRecipients(res.recipients || []);
      setPreviewCount(null);
    } catch (e) {
      setFormError(resolveGroupParticipantsErrorMessage(e, 'Arquivo Excel inválido'));
    } finally {
      setExcelImporting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setFormError(null);
    try {
      const res = await previewSparkAudience(sectorId, audiencePayload);
      setPreviewCount(res.estimatedCount ?? 0);
      const notes: string[] = [];
      if (res.overLimit && res.messagingLimit) notes.push(`Acima do limite de ${res.messagingLimit} clientes novos a cada 24 h do seu portfólio na Meta.`);
      if (res.optedOut) notes.push(`${res.optedOut} pararam de receber marketing e serão pulados nas campanhas de marketing.`);
      setPreviewNote(notes.length ? notes.join(' ') : null);
    } catch (e) {
      setFormError(resolveGroupParticipantsErrorMessage(e, 'Não foi possível calcular a prévia do público'));
      setPreviewCount(null);
      setPreviewNote(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setFormError(null);
    try {
      if (!formName.trim()) throw new Error('Informe o nome da campanha');
      if (!formTemplateId) throw new Error('Selecione um modelo de mensagem aprovado do WhatsApp');
      if (formRecipients.length === 0 && formContactIds.size === 0 && formColumnIds.size === 0) {
        throw new Error('Escolha colunas do funil, contatos do CRM ou envie uma planilha');
      }
      const missing = (templateDetail?.commonVariables || [])
        .filter((v) => v.required && !(formVars[v.key] || '').trim())
        .map((v) => v.label || v.key);
      if (missing.length) throw new Error(`Variáveis obrigatórias: ${missing.join(', ')}`);

      await createSparkCampaign(sectorId, {
        name: formName.trim(),
        templateId: formTemplateId,
        platform: 'whatsapp',
        cadence: formCadence,
        entrypointId: formEntrypointId || undefined,
        recipients: formRecipients,
        contactIds: Array.from(formContactIds),
        columnIds: Array.from(formColumnIds),
        commonVariables: formVars,
        aiAgentId: formAiReplyEnabled ? formAiAgentId : null,
        aiReplyEnabled: formAiReplyEnabled && !!formAiAgentId,
      });
      setShowCreate(false);
      await loadList();
    } catch (e) {
      setFormError(resolveGroupParticipantsErrorMessage(e, 'Não foi possível criar a campanha'));
    } finally {
      setCreating(false);
    }
  };

  const handleConfirm = async (): Promise<boolean | void> => {
    if (!selectedId || !confirmAction) return;
    setConfirmLoading(true);
    try {
      if (confirmAction === 'start' || confirmAction === 'over_limit') {
        await startSparkCampaign(sectorId, selectedId, detail?.campaign.commonVariables, confirmAction === 'over_limit' ? true : undefined);
      } else {
        await cancelSparkCampaign(sectorId, selectedId);
      }
      setConfirmAction(null);
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      const over = confirmAction === 'start' ? overLimitInfo(e) : null;
      if (over) {
        // Audiência acima do limite do portfólio: pede confirmação e reenvia com acceptOverLimit.
        setOverLimitMessage(over.message);
        setConfirmAction('over_limit');
        return false; // o modal continua aberto, agora pedindo a confirmação
      } else {
        setError(resolveGroupParticipantsErrorMessage(e, 'Não foi possível concluir a ação'));
        setConfirmAction(null);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const tabs = (
    <div className="campaign-header-row">
      <div className="campaign-section">
        <h2 className="campaign-section__title">Campanha</h2>
        <nav className="campaign-tabs">
          <button className="campaign-tabs__item campaign-tabs__item--active" type="button">
            Campanhas
          </button>
          {hasEmailModule && onSwitchToEmail && (
            <>
              <span className="campaign-tabs__separator" />
              <button className="campaign-tabs__item" type="button" onClick={onSwitchToEmail}>
                Campanhas de e-mail
              </button>
            </>
          )}
        </nav>
      </div>
    </div>
  );

  if (selectedId != null && detail) {
    const c = detail.campaign;
    const exec = detail.execution;
    const progress = exec?.progress ?? (exec?.targetCount && exec.targetCount > 0
      ? Math.round(((exec.sentCount ?? 0) / exec.targetCount) * 100)
      : 0);
    return (
      <div className="broadcast-page">
        <div className="broadcast-header">
          {tabs}
          <div className="broadcast-header-actions">
            <button type="button" className="btn-secondary" onClick={() => setSelectedId(null)}>
              Voltar
            </button>
            {(c.status === 'draft' || c.status === 'pending' || c.status === 'paused') && (
              <button type="button" className="btn-primary" onClick={() => setConfirmAction('start')}>
                {c.status === 'paused' ? 'Retomar' : 'Iniciar'}
              </button>
            )}
            {(c.status === 'processing' || c.status === 'running' || c.status === 'draft' || c.status === 'paused') && (
              <button type="button" className="btn-secondary" onClick={() => setConfirmAction('cancel')}>
                Cancelar
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{ margin: '0 24px 12px', color: 'var(--danger-color)', fontSize: 14 }}>{error}</div>
        )}

        <div className="broadcast-content" style={{ padding: 24 }}>
          {detailLoading ? (
            <div className="broadcast-loading"><div className="spinner" />Carregando…</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>{c.name}</h3>
              <div className="broadcast-email-detail-meta" style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-tertiary)' }}>
                Status: <span className={statusClass(c.status)}>{STATUS_LABEL[c.status] || c.status}</span>
                {' · '}Público: {AUDIENCE_LABEL[c.audienceMode] || c.audienceMode}
                {' · '}Destinatários: {c.recipientCount}
                {' · '}Cadência: {c.cadence}/min
                {c.aiAgentId ? ' · IA da campanha ativa' : ''}
              </div>
              {exec && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span>Envio: {STATUS_LABEL[exec.status] || exec.status}</span>
                    <span>{progress}%</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--border-color)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, Math.max(0, progress))}%`, height: '100%', background: 'var(--primary-color)' }} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    Enviadas {exec.sentCount ?? 0} / {exec.targetCount ?? '—'}
                    {exec.deliveredCount != null ? ` · Entregues ${exec.deliveredCount}` : ''}
                    {exec.readCount != null ? ` · Lidas ${exec.readCount}` : ''}
                    {exec.failedCount != null ? ` · Com falha ${exec.failedCount}` : ''}
                    {exec.skippedCount ? ` · Pulados ${exec.skippedCount}` : ''}
                  </div>
                  {exec.note ? <div role="note" style={{ fontSize: 12, marginTop: 6 }}>{exec.note}</div> : null}
                </div>
              )}
              {detail.recipients.length > 0 && (
                <div>
                  <h4>Destinatários</h4>
                  <table className="recipients-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Telefone</th>
                        <th>Inscrito na IA</th>
                        <th>Envio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.recipients.slice(0, 200).map((r) => (
                        <tr key={r.id}>
                          <td>{r.name || '—'}</td>
                          <td>{r.phone}</td>
                          <td>{r.enrolledAt ? 'Sim' : '—'}</td>
                          <td title={r.error || undefined}>{r.status ? RECIPIENT_STATUS_LABEL[r.status] || r.status : '—'}{r.error ? `: ${r.error}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <ConfirmModal
          open={confirmAction !== null}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
          title={confirmAction === 'over_limit' ? 'Audiência acima do limite' : confirmAction === 'start' ? 'Iniciar campanha' : 'Cancelar campanha'}
          message={
            confirmAction === 'over_limit'
              ? overLimitMessage
              : confirmAction === 'start'
                ? 'Iniciar o envio desta campanha oficial do WhatsApp? O modelo aprovado é enviado a cada destinatário, no ritmo que a Meta permite para o número.'
                : 'Interromper esta campanha? Os destinatários pendentes não receberão a mensagem.'
          }
          variant={confirmAction === 'cancel' ? 'danger' : confirmAction === 'over_limit' ? 'warning' : 'default'}
          confirmLabel={confirmAction === 'cancel' ? 'Cancelar campanha' : confirmAction === 'over_limit' ? 'Iniciar mesmo assim' : 'Iniciar'}
          cancelLabel="Fechar"
          loading={confirmLoading}
        />
      </div>
    );
  }

  const previewBody = templatePreviewText(templateDetail);

  return (
    <div className="broadcast-page">
      <div className="broadcast-header">
        {tabs}
        <div className="broadcast-header-actions">
          <button type="button" className="btn-secondary" onClick={loadList}>
            <FiRefreshCw size={14} /> Atualizar
          </button>
          <button type="button" className="btn-primary" onClick={openCreate}>
            <FiPlus size={14} /> Nova campanha
          </button>
        </div>
      </div>

      {error && (
        <div style={{ margin: '0 24px 12px', color: 'var(--danger-color)', fontSize: 14 }}>{error}</div>
      )}

      <div className="broadcast-content">
        {loading ? (
          <div className="broadcast-loading"><div className="spinner" />Carregando…</div>
        ) : items.length === 0 ? (
          <div className="broadcast-empty">
            <h3>Nenhuma campanha ainda</h3>
            <p>Crie uma campanha com um modelo de mensagem aprovado do WhatsApp e leads do seu funil, do CRM ou de uma planilha.</p>
            <button type="button" className="btn-secondary" style={{ marginTop: 12 }} onClick={() => setShowTemplates(true)}>
              Modelos de mensagem
            </button>
          </div>
        ) : (
          <div className="broadcast-list">
            {items.map((b) => (
              <button
                key={b.id}
                type="button"
                className="broadcast-card"
                onClick={() => setSelectedId(b.id)}
                style={{ textAlign: 'left', width: '100%', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <strong>{b.name}</strong>
                  <span className={statusClass(b.status)}>{STATUS_LABEL[b.status] || b.status}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 6 }}>
                  {b.recipientCount} destinatários · {AUDIENCE_LABEL[b.audienceMode] || b.audienceMode}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <div className="modal-overlay" onClick={() => !creating && setShowCreate(false)}>
          <div className="modal-content" style={{ maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Nova campanha</h2>
              <button type="button" className="btn-icon" onClick={() => setShowCreate(false)} aria-label="Fechar">
                <FiX size={18} />
              </button>
            </div>

            {formError && (
              <div style={{ marginBottom: 12, padding: 10, background: 'var(--danger-bg, #fee)', borderRadius: 8, color: 'var(--danger-color)', fontSize: 13 }}>
                {formError}
              </div>
            )}

            <label style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
              Nome
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 8 }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
              Modelo de mensagem aprovado do WhatsApp
              <select
                value={formTemplateId}
                onChange={(e) => setFormTemplateId(e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 8 }}
              >
                <option value="">Selecione um modelo…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.linePhone ? `${t.name} · ${t.linePhone}` : t.name}
                  </option>
                ))}
              </select>
            </label>
            {templates.length === 0 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -6, marginBottom: 12 }}>
                Nenhum modelo de mensagem aprovado pela Meta foi encontrado.{' '}
                <button type="button" className="btn-secondary" onClick={() => setShowTemplates(true)}>
                  Criar modelo de mensagem
                </button>
              </p>
            )}

            {previewBody && (
              <div style={{ marginBottom: 12, padding: 12, background: 'var(--bg-elevated)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {previewBody}
              </div>
            )}

            {templateDetail && (templateDetail.commonVariables?.length ?? 0) > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Variáveis do modelo</div>
                {templateDetail.commonVariables.map((v) => (
                  <label key={v.key} style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                    {v.label}{v.required ? ' *' : ''}
                    <input
                      type="text"
                      value={formVars[v.key] ?? ''}
                      onChange={(e) => setFormVars((prev) => ({ ...prev, [v.key]: e.target.value }))}
                      style={{ width: '100%', marginTop: 4, padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6 }}
                    />
                  </label>
                ))}
              </div>
            )}

            {entrypoints.length > 1 && (
              <label style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                Número oficial do WhatsApp
                <select
                  value={formEntrypointId}
                  onChange={(e) => setFormEntrypointId(e.target.value)}
                  style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 8 }}
                >
                  {entrypoints.map((ep) => (
                    <option key={ep.entrypointId} value={ep.entrypointId}>
                      {ep.label || ep.linePhone || ep.entrypointId}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div style={{ marginBottom: 12, padding: 12, border: '1px solid var(--border-color)', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Colunas do funil</div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>
                Envia para todos os leads que estão agora nas etapas do CRM selecionadas.
              </p>
              {columns.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: 0 }}>Nenhuma coluna do funil encontrada.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {columns.map((col) => {
                    const id = Number(col.id);
                    const checked = formColumnIds.has(id);
                    return (
                      <label
                        key={col.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: `1px solid ${checked ? 'var(--primary-color)' : 'var(--border-color)'}`,
                          background: checked ? 'var(--bg-elevated)' : 'var(--bg-card)',
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleColumn(id)} />
                        {col.name}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12, padding: 12, border: '1px solid var(--border-color)', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Contatos do CRM</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FiSearch size={14} />
                <input
                  type="search"
                  placeholder="Buscar contatos…"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 13 }}
                />
                {formContactIds.size > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{formContactIds.size} selecionados</span>
                )}
              </div>
              <div style={{ maxHeight: 180, overflow: 'auto' }}>
                {contactsLoading ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Carregando contatos…</div>
                ) : contactsError ? (
                  <div style={{ fontSize: 12, color: 'var(--danger-color)' }}>{contactsError}</div>
                ) : contactResults.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Nenhum contato encontrado.</div>
                ) : (
                  contactResults.map((c) => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={formContactIds.has(c.id)} onChange={() => toggleContact(c.id)} />
                      <span>{c.name}</span>
                      <span style={{ color: 'var(--text-tertiary)' }}>{c.phone}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Planilha (colunas Name e Phone Number)</div>
              <input
                id="spark-xlsx-input"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => handleExcel(e.target.files?.[0] ?? null)}
              />
              <label htmlFor="spark-xlsx-input" className="btn-secondary" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                {excelImporting ? <LoadingSpinner size="sm" color="white" /> : <FiUpload size={14} />}
                Enviar Excel
              </label>
              {formRecipients.length > 0 && (
                <span style={{ marginLeft: 10, fontSize: 13, color: 'var(--text-tertiary)' }}>
                  {formRecipients.length} contatos carregados
                </span>
              )}
            </div>

            <label style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
              Cadência (mensagens por minuto)
              <input
                type="number"
                min={1}
                value={formCadence}
                onChange={(e) => setFormCadence(Math.max(1, Number(e.target.value) || 1))}
                style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 8 }}
              />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                O Mends respeita o limite de envio do número na Meta e 1 mensagem a cada 6 s para o mesmo cliente; a cadência só deixa o envio mais devagar. Aqui não existe a janela de envio das 7h às 22h da conexão não oficial.
              </p>
            </label>

            <label style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
              Agente de IA da campanha
              <select
                value={formAiAgentId ?? ''}
                onChange={(e) => setFormAiAgentId(e.target.value ? Number(e.target.value) : null)}
                style={{ width: '100%', marginTop: 4, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: 8 }}
              >
                <option value="">Nenhum (sem IA na campanha)</option>
                {aiAgents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={formAiReplyEnabled}
                onChange={(e) => setFormAiReplyEnabled(e.target.checked)}
              />
              Ativar respostas da IA quando o lead responder
            </label>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <button type="button" className="btn-secondary" onClick={handlePreview} disabled={previewLoading}>
                {previewLoading ? 'Calculando…' : 'Prévia do público'}
              </button>
              {previewCount != null && (
                <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{previewCount} destinatários{previewNote ? ` · ${previewNote}` : ''}</span>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-secondary" disabled={creating} onClick={() => setShowCreate(false)}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" disabled={creating} onClick={handleCreate}>
                {creating ? <LoadingSpinner size="sm" color="white" /> : 'Criar rascunho'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showTemplates && (
        <SparkMessageTemplatesPanel
          sectorId={sectorId}
          onClose={() => {
            setShowTemplates(false);
            if (showCreate) {
              listSparkTemplates(sectorId).then(setTemplates).catch(() => undefined);
            }
          }}
        />
      )}
    </div>
  );
};

export default SparkCampaignsPanel;
